import {
  activateBrandTenant,
  authenticatedBrandScope,
  publicBrandScope,
  rememberBrand,
} from "@/lib/brand-cache";
import type { CourseSettings } from "@/courses/settings-contract";
import type { CoursePresentation } from "@/courses/presentation-contract";
// src/api.ts
//
// Camada de chamada HTTP única do front (better-fetch). Fronteira única com o
// back: todo request passa aqui. Preserva as GARANTIAS do education (não regride
// para o modelo simples): valida a resposta com Zod, propaga `traceId` no erro,
// e toda MUTAÇÃO carrega `Idempotency-Key` (invariante anti-placebo). O envelope
// do back é { data } no sucesso e { error: { code, message, retryable } } no erro.
import { createFetch } from "@better-fetch/fetch";
import { z } from "zod";
import { reportSessionStatus } from "@/lib/session-expiration";
import { LearnerProgressSchema, type LearnerProgress } from "@/learner-progress-contract";
import {
  CompanySchema,
  CompanyContextSchema,
  type CompanyContext,
} from "@/contracts";
import {
  ShowcaseRecordSchema,
  type ShowcaseRecord,
  type ShowcaseStatus,
  type Block,
} from "@/showcases/block-contract";
import {
  SessionSchema,
  ProgramSchema,
  ProgramVitrineSchema,
  CourseSchema,
  CurriculumVersionSchema,
  CohortSchema,
  EnrollmentSchema,
  InviteSchema,
  CreateInviteResultSchema,
  InvitePublicSchema,
  ProvisionResultSchema,
  LessonSchema,
  LessonMaterialSchema,
  MaterialDownloadStatSchema,
  WatchSchema,
  OutcomeContractSchema,
  MetricObservationSchema,
  CommitmentSchema,
  InterventionCaseSchema,
  ApplicableSchema,
  TwinSchema,
  ProposalSchema,
  AnalyticsOverviewSchema,
  AnalyticsCoursesSchema,
  SpaceSchema,
  SpaceMemberSchema,
  PostSchema,
  FeedPageSchema,
  CommentSchema,
  ReactionResultSchema,
  SaveResultSchema,
  EngagementSummarySchema,
  UserSearchResultSchema,
  UserProfileSchema,
  NotificationListSchema,
  UnreadCountSchema,
  DmThreadSchema,
  DmMessageSchema,
  DmSendResultSchema,
  ShowcaseSchema,
  ProgramPreviewSchema,
  CoursePreviewSchema,
  RankingEntrySchema,
  MyGamificationSchema,
  BadgeSchema,
  StreakResultSchema,
  AdminDashboardSchema,
  ActivityPointSchema,
  DashboardPostSchema,
  FeatureFlagSchema,
  BrandingSchema,
  BrandSubdomainSchema,
  PublicBrandingSchema,
  TagSchema,
  LessonCommentSchema,
  ModerationCommentSchema,
  WalletMeSchema,
  WalletSummarySchema,
  UserScoreListSchema,
  AdminUserListSchema,
  AdminUserDetailSchema,
  ImportResultSchema,
  AdminUserUpdateSchema,
  AdminUserRemovalSchema,
  AdminUserTempPasswordSchema,
  AdSchema,
  ModerationActionSchema,
  AuditEventSchema,
  AuditPageSchema,
  AuditReviewSchema,
  DomainCheckSchema,
  InsightDefinitionSchema,
  ApiKeySchema,
  ApiScopeSchema,
  ApiKeyCreatedSchema,
  McpKeySchema,
  McpKeyCreatedSchema,
  OutboundWebhookSchema,
  WebhookDeliverySchema,
  AutomationSchema,
  ZoomStatusSchema,
  PageListItemSchema,
  PageDetailSchema,
  EmailTemplateListItemSchema,
  RenderedTemplateSchema,
  MobileMenuSchema,
  type Session,
  type Space,
  type SpaceMember,
  type Post,
  type FeedPage,
  type Comment,
  type ReactionResult,
  type SaveResult,
  type EngagementSummary,
  type UserSearchResult,
  type UserProfile,
  type NotificationList,
  type UnreadCount,
  type DmThread,
  type DmMessage,
  type DmSendResult,
  type Showcase,
  type ShowcaseAppearance,
  type ProgramPreview,
  type CoursePreview,
  type RankingEntry,
  type MyGamification,
  type Badge,
  type StreakResult,
  type AdminDashboard,
  type ActivityPoint,
  type DashboardPost,
  type FeatureFlag,
  type Branding,
  type BrandSubdomain,
  type PublicBranding,
  type BrandingSettings,
  type Tag,
  type LessonComment,
  type ModerationComment,
  type WalletMe,
  type WalletSummary,
  type UserScoreList,
  type AdminUserList,
  type AdminUserDetail,
  type ImportResult,
  type AdminUserUpdate,
  type AdminUserRemoval,
  type Ad,
  type ModerationAction,
  type AuditEvent,
  type AuditPage,
  type AuditReview,
  type DomainCheck,
  type InsightDefinition,
  type ApiKey,
  type ApiScope,
  type ApiKeyCreated,
  type McpKey,
  type McpKeyCreated,
  type OutboundWebhook,
  type WebhookDelivery,
  type Automation,
  type ZoomStatus,
  type PageListItem,
  type PageDetail,
  type PageNav,
  type EmailTemplateListItem,
  type RenderedTemplate,
  type MobileMenu,
  type MobileMenuItem,
  type Program,
  type ProgramHero,
  type ProgramVitrine,
  type Course,
  type CurriculumVersion,
  type CurriculumContent,
  type Cohort,
  type Enrollment,
  type Invite,
  type InvitePublic,
  type ProvisionResult,
  type Lesson,
  type LessonMaterial,
  type MaterialDownloadStat,
  type Watch,
  type OutcomeContract,
  type MetricObservation,
  type Commitment,
  type InterventionCase,
  type Applicable,
  type Twin,
  type Proposal,
  type AnalyticsOverview,
  type AnalyticsCourses,
  PlatformOrganizationSchema,
  AccessBadgeSchema,
  MyHomeBadgeSchema,
  type HomeLink,
  AccessBadgeDetailSchema,
  AccessBadgeContentsSchema,
  AccessBadgeGrantPageSchema,
  BulkActionResultSchema,
  AccessBadgeGrantResultSchema,
  AccessBadgeGrantManyResultSchema,
  AccessBadgeHistoryPageSchema,
  AccessBadgeOriginsSchema,
  MyAccessBadgeSchema,
  type AccessBadge,
  type AccessBadgeDetail,
  type AccessBadgeGrant,
  type BulkActionResult,
} from "@/contracts";
import type { FavoriteTerm, Term } from "@/lib/badge-term";
import { usePreferencesStore } from "@/preferences-store";
import { resolveApiBaseUrl } from "@/lib/api-base-url";
import { checkUpload, type UploadPurpose } from "@/lib/upload-policy";

// Re-export dos tipos de domínio para as telas consumirem a partir da fronteira
// única (mesma origem das funções de chamada). `export type *` reflete o
// contracts.ts inteiro — nada de lista mantida à mão (fonte dos erros TS
// recorrentes e do drift quando um tipo novo era esquecido aqui).
export type * from "@/contracts";
// Schemas (valores) que as telas usam para parse client-side também saem da
// fronteira — evita `import ... from "@/contracts"` cru nas telas.
export { CurriculumContentSchema } from "@/contracts";

export const API_BASE_URL = resolveApiBaseUrl();

const api = createFetch({
  baseURL: API_BASE_URL,
  retry: 0, // sem retry silencioso — falha é honesta
  headers: { Accept: "application/json" },
  credentials: "include", // cookie de sessão do Better Auth
});

// Papéis da organização (mesma hierarquia do back).
export const ORG_ROLES = ["mentorado", "mentor", "admin"] as const;
export type OrgRole = (typeof ORG_ROLES)[number];
export const ROLE_LABELS: Record<OrgRole, string> = {
  mentorado: "Mentorado",
  mentor: "Mentor",
  admin: "Gestor",
};


// Códigos de erro de domínio do back → mensagem PT acionável.
const DOMAIN_ERROR_MESSAGES: Record<string, string> = {
  IDEMPOTENCY_KEY_REQUIRED: "Falha interna: mutação sem chave de idempotência.",
  IDEMPOTENCY_CONFLICT:
    "Esta ação já foi enviada com dados diferentes. Recarregue e tente de novo.",
  NO_ACTIVE_ORGANIZATION: "Sua sessão não tem uma organização ativa.",
  AUTHENTICATION_REQUIRED: "Sua sessão expirou. Entre novamente.",
  AUTHORIZATION_DENIED: "Você não tem permissão para esta ação.",
  VALIDATION_FAILED: "Dados inválidos. Revise e tente de novo.",
  NOT_FOUND: "Recurso não encontrado.",
  CONFLICT: "A ação conflita com o estado atual do recurso.",
};

// Erro de API rico (preserva code/traceId/retryable do contrato do education).
export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly retryable: boolean,
    public readonly traceId: string | null,
    // A mensagem do PRÓPRIO back, antes da troca pela amigável genérica do código:
    // "certificado inválido: falta o bloco Código" diz o que corrigir; "Dados
    // inválidos" não. Quem precisa do detalhe opta por ela (toastError).
    public readonly serverMessage: string | null = null,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

type ErrorBody = { error?: { code?: string; message?: string; retryable?: boolean } };

function toApiError(path: string, err: unknown, traceId: string | null): ApiError {
  const e = err as { status?: number } & ErrorBody & { message?: string; statusText?: string };
  const code = e.error?.code ?? "UNKNOWN";
  const friendly =
    (code && DOMAIN_ERROR_MESSAGES[code]) ??
    e.error?.message ??
    e.statusText ??
    e.message ??
    "erro desconhecido";
  return new ApiError(
    code,
    `${path}: ${friendly}`,
    e.status ?? 0,
    e.error?.retryable ?? false,
    traceId,
    e.error?.message ?? null,
  );
}

// Captura os headers da resposta via hook (closure por chamada — sem corrida).
function headerCapture() {
  const box: { headers: Headers | null } = { headers: null };
  return {
    box,
    onResponse: (ctx: { response: Response }) => {
      box.headers = ctx.response.headers;
      reportSessionStatus(ctx.response.status);
    },
  };
}

// GET tipado + validado. Lança ApiError (integra com queryFn do React Query).
export async function query<T>(
  path: string,
  schema: z.ZodType<T>,
  params?: Record<string, string>,
): Promise<T> {
  const actAs = usePreferencesStore.getState().viewAs;
  const cap = headerCapture();
  const { data, error } = await api<{ data: unknown }>(path, {
    params,
    onResponse: cap.onResponse,
    // view-as também no read: o back rebaixa os scopes → dados coerentes com a UI.
    headers: actAs ? { "x-act-as": actAs } : {},
  });
  const traceId = cap.box.headers?.get("x-trace-id") ?? null;
  if (error) throw toApiError(`GET ${path}`, error, traceId);
  return schema.parse(data?.data);
}

// Resultado de uma mutação: o dado + se foi replay idempotente (header do back).
export interface CommandResult<T> {
  data: T;
  replayed: boolean;
  traceId: string | null;
}

// Mutação idempotente (POST ou PATCH): gera Idempotency-Key, injeta x-act-as
// (view-as) e traceparent, valida a resposta. INVARIANTE: toda mutação passa aqui.
async function mutate<T>(
  method: "POST" | "PATCH" | "PUT",
  path: string,
  body: unknown,
  schema: z.ZodType<T>,
  idempotencyKey: string = crypto.randomUUID(),
): Promise<CommandResult<T>> {
  const actAs = usePreferencesStore.getState().viewAs;
  const cap = headerCapture();
  const { data, error } = await api<{ data: unknown }>(path, {
    method,
    body,
    onResponse: cap.onResponse,
    headers: {
      "content-type": "application/json",
      "idempotency-key": idempotencyKey,
      traceparent: `00-${crypto.randomUUID().replace(/-/g, "")}-${crypto.randomUUID().slice(0, 16).replace(/-/g, "")}-01`,
      ...(actAs ? { "x-act-as": actAs } : {}),
    },
  });
  const traceId = cap.box.headers?.get("x-trace-id") ?? null;
  if (error) throw toApiError(`${method} ${path}`, error, traceId);
  return {
    data: schema.parse(data?.data),
    replayed: cap.box.headers?.get("x-idempotent-replayed") === "true",
    traceId,
  };
}

export function command<T>(
  path: string,
  body: unknown,
  schema: z.ZodType<T>,
  idempotencyKey?: string,
): Promise<CommandResult<T>> {
  return mutate("POST", path, body, schema, idempotencyKey);
}

export function patch<T>(
  path: string,
  body: unknown,
  schema: z.ZodType<T>,
  idempotencyKey?: string,
): Promise<CommandResult<T>> {
  return mutate("PATCH", path, body, schema, idempotencyKey);
}

export function put<T>(
  path: string,
  body: unknown,
  schema: z.ZodType<T>,
  idempotencyKey?: string,
): Promise<CommandResult<T>> {
  return mutate("PUT", path, body, schema, idempotencyKey);
}

// DELETE (204, sem corpo). É MUTAÇÃO → carrega Idempotency-Key + traceparent
// (o back exige a key; sem ela todo DELETE retornava 422). Injeta view-as.
export async function remove(
  path: string,
  idempotencyKey: string = crypto.randomUUID(),
): Promise<void> {
  const actAs = usePreferencesStore.getState().viewAs;
  const cap = headerCapture();
  const { error } = await api(path, {
    method: "DELETE",
    onResponse: cap.onResponse,
    headers: {
      "idempotency-key": idempotencyKey,
      traceparent: `00-${crypto.randomUUID().replace(/-/g, "")}-${crypto.randomUUID().slice(0, 16).replace(/-/g, "")}-01`,
      ...(actAs ? { "x-act-as": actAs } : {}),
    },
  });
  if (error) throw toApiError(`DELETE ${path}`, error, cap.box.headers?.get("x-trace-id") ?? null);
}

// --- Endpoints ---

export const sessionQueryKey = ["session"] as const;
export function getSession(): Promise<Session> {
  return query("/v1/session", SessionSchema).then((session) => {
    activateBrandTenant(session.identity.tenantId);
    return session;
  });
}

// --- Comunidade: espaços ---
export const spacesQueryKey = ["spaces"] as const;
export function getSpaces(): Promise<Space[]> {
  return query("/v1/spaces", SpaceSchema.array());
}
export function getSpace(id: string): Promise<Space> {
  return query(`/v1/spaces/${id}`, SpaceSchema);
}
export function createSpace(input: {
  slug: string;
  name: string;
  description?: string;
  kind: "community" | "course" | "system";
  visibility?: "public" | "members" | "private";
  parentId?: string;
  freeSpace?: boolean;
  onlyModeratorsCanPost?: boolean;
}): Promise<CommandResult<Space>> {
  return command("/v1/spaces", input, SpaceSchema);
}
export function createSpaceGroup(input: {
  slug: string;
  name: string;
  description?: string;
  parentId: string;
}): Promise<CommandResult<Space>> {
  return command("/v1/spaces/groups", input, SpaceSchema);
}
export function joinSpace(id: string): Promise<CommandResult<Space>> {
  return command(`/v1/spaces/${id}/join`, {}, SpaceSchema);
}
export function updateSpace(
  id: string,
  input: {
    name: string;
    description: string;
    visibility: "public" | "members" | "private";
    icon: string;
    coverUrl?: string | null;
    position: number;
    freeSpace: boolean;
    onlyModeratorsCanPost: boolean;
  },
): Promise<CommandResult<Space>> {
  return patch(`/v1/spaces/${id}`, input, SpaceSchema);
}
// Arquivado some para todos menos gestores; desarquivar devolve a visibilidade anterior.
// confirm: o gestor já viu quantos cursos e trilhas ficam ocultos (409
// SPACE_HAS_CONTENT na primeira tentativa).
export function archiveSpace(id: string, confirm = false): Promise<CommandResult<Space>> {
  return command(`/v1/spaces/${id}/archive${confirm ? "?confirm=true" : ""}`, {}, SpaceSchema);
}
export function unarchiveSpace(id: string): Promise<CommandResult<Space>> {
  return command(`/v1/spaces/${id}/unarchive`, {}, SpaceSchema);
}
// Só espaço arquivado e vazio (o back recusa com 409 e o motivo).
export function deleteSpace(id: string): Promise<void> {
  return remove(`/v1/spaces/${id}`);
}
export function leaveSpace(id: string): Promise<CommandResult<Space>> {
  return command(`/v1/spaces/${id}/leave`, {}, SpaceSchema);
}
export function getSpaceMembers(id: string): Promise<SpaceMember[]> {
  return query(`/v1/spaces/${id}/members`, SpaceMemberSchema.array());
}
// Incluir vínculo novo; renovação usa sua própria ação e endpoint.
export function addSpaceMember(
  spaceId: string,
  input: { userId: string; expiresAt?: string | null; term?: Term },
): Promise<CommandResult<SpaceMember[]>> {
  return command(`/v1/spaces/${spaceId}/members/create`, input, SpaceMemberSchema.array());
}
export function renewSpaceMember(
  spaceId: string,
  input: { userId: string; expiresAt?: string | null; term?: Term },
): Promise<CommandResult<SpaceMember[]>> {
  const { userId, ...renewal } = input;
  return command(
    `/v1/spaces/${spaceId}/members/${userId}/renew`,
    renewal,
    SpaceMemberSchema.array(),
  );
}
// Remover membro do espaço (community.spaces.removeMember).
export function removeSpaceMember(
  spaceId: string,
  userId: string,
): Promise<CommandResult<SpaceMember[]>> {
  return command(`/v1/spaces/${spaceId}/members/${userId}/remove`, {}, SpaceMemberSchema.array());
}

// --- Social: posts / feed ---
export const feedQueryKey = ["feed"] as const;
export type FeedFilter = "recentes" | "destaques" | "em-alta" | "seguindo";
export function getFeed(params: {
  authorUserId?: string;
  filter?: FeedFilter;
  spaceId?: string;
  cursor?: string;
  limit?: number;
  category?: string;
}): Promise<FeedPage> {
  const q = new URLSearchParams();
  if (params.authorUserId) q.set("authorUserId", params.authorUserId);
  if (params.filter) q.set("filter", params.filter);
  if (params.spaceId) q.set("spaceId", params.spaceId);
  if (params.cursor) q.set("cursor", params.cursor);
  if (params.limit) q.set("limit", String(params.limit));
  if (params.category) q.set("category", params.category);
  const qs = q.toString();
  return query(`/v1/feed${qs ? `?${qs}` : ""}`, FeedPageSchema).then((page) => {
    // Back antigo pode ignorar parâmetros desconhecidos: nunca mostrar outros autores no perfil.
    if (
      params.authorUserId &&
      page.items.some((post) => post.authorUserId !== params.authorUserId)
    ) {
      throw new Error("Não foi possível carregar as publicações deste perfil.");
    }
    return page;
  });
}
export const postQueryKey = (id: string) => ["post", id] as const;
export function getPost(id: string): Promise<Post> {
  return query(`/v1/posts/${id}`, PostSchema);
}
export function createPost(
  input: {
    spaceId: string;
    body: string;
    category?: string;
    mentions?: string[];
    bodyFormat?: "plain" | "markdown";
    media?: Array<{
      kind: "image" | "video" | "audio" | "file" | "link";
      url: string;
      title?: string;
      assetKey?: string;
    }>;
  },
  idempotencyKey?: string,
): Promise<CommandResult<Post>> {
  return command("/v1/posts", input, PostSchema, idempotencyKey);
}
export function updatePost(
  id: string,
  input: {
    body?: string;
    category?: string;
    bodyFormat?: "plain" | "markdown";
    media?: Array<{
      kind: "image" | "video" | "audio" | "file" | "link";
      url: string;
      title?: string;
      assetKey?: string;
    }>;
  },
  idempotencyKey?: string,
): Promise<CommandResult<Post>> {
  return patch(`/v1/posts/${id}`, input, PostSchema, idempotencyKey);
}
export function removePost(id: string): Promise<CommandResult<Post>> {
  return command(`/v1/posts/${id}/remove`, {}, PostSchema);
}
export function pinPost(id: string, pinned: boolean): Promise<CommandResult<Post>> {
  return command(`/v1/posts/${id}/${pinned ? "pin" : "unpin"}`, {}, PostSchema);
}

// --- Social: engajamento (comentários, reações, salvos) ---
export const commentsQueryKey = (postId: string) => ["post", postId, "comments"] as const;
export const engagementQueryKey = (postId: string) => ["post", postId, "engagement"] as const;
export const savedPostsQueryKey = ["saved-posts"] as const;

export function getComments(postId: string): Promise<Comment[]> {
  return query(`/v1/posts/${postId}/comments`, CommentSchema.array());
}
export function createComment(
  postId: string,
  input: { body: string; parentId?: string; mentions?: string[] },
  idempotencyKey?: string,
): Promise<CommandResult<Comment>> {
  return command(`/v1/posts/${postId}/comments`, input, CommentSchema, idempotencyKey);
}
export function removeComment(commentId: string): Promise<CommandResult<Comment>> {
  // O back devolve o CommentRow completo (status removed) — fase-34.
  return command(`/v1/comments/${commentId}/remove`, {}, CommentSchema);
}
export function reactPost(postId: string, add: boolean): Promise<CommandResult<ReactionResult>> {
  return command(`/v1/posts/${postId}/reactions${add ? "" : ":remove"}`, {}, ReactionResultSchema);
}
export function reactComment(
  commentId: string,
  add: boolean,
): Promise<CommandResult<ReactionResult>> {
  return command(
    `/v1/comments/${commentId}/reactions${add ? "" : ":remove"}`,
    {},
    ReactionResultSchema,
  );
}
export function savePost(postId: string, save: boolean): Promise<CommandResult<SaveResult>> {
  return command(`/v1/posts/${postId}/${save ? "save" : "unsave"}`, {}, SaveResultSchema);
}
export function getEngagement(postId: string): Promise<EngagementSummary> {
  return query(`/v1/posts/${postId}/engagement`, EngagementSummarySchema);
}
export async function getSavedPostsPage(cursor?: string): Promise<FeedPage> {
  try {
    return await query(
      `/v1/saved-posts/page${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`,
      FeedPageSchema,
    );
  } catch (error) {
    // Deploys independentes: o endpoint legado continua funcionando no back antigo.
    if (!cursor && error instanceof ApiError && error.status === 404) {
      return { items: await getSavedPosts(), nextCursor: null };
    }
    throw error;
  }
}
export function getSavedPosts(): Promise<Post[]> {
  return query("/v1/saved-posts", PostSchema.array());
}

// --- Social: grafo (seguir) + diretório de usuários ---
// spaceId: em espaço privado, a busca de menção sugere só membros dele.
export function searchUsers(q: string, spaceId?: string): Promise<UserSearchResult[]> {
  const space = spaceId ? `&spaceId=${encodeURIComponent(spaceId)}` : "";
  return query(
    `/v1/users/search?q=${encodeURIComponent(q)}${space}`,
    UserSearchResultSchema.array(),
  );
}
export function getUsersByIds(ids: string[]): Promise<UserSearchResult[]> {
  if (ids.length === 0) return Promise.resolve([]);
  return query(
    `/v1/users?ids=${ids.map(encodeURIComponent).join(",")}`,
    UserSearchResultSchema.array(),
  );
}
export function getUserProfile(id: string): Promise<UserProfile> {
  return query(`/v1/users/${id}`, UserProfileSchema);
}
export function userProfileQueryKey(id: string) {
  return ["user", id] as const;
}
export function followUser(id: string): Promise<CommandResult<UserProfile>> {
  return command(`/v1/users/${id}/follow`, {}, UserProfileSchema);
}
export function unfollowUser(id: string): Promise<CommandResult<UserProfile>> {
  return command(`/v1/users/${id}/unfollow`, {}, UserProfileSchema);
}

// --- Social: notificações (o sino) ---
export const notificationsQueryKey = ["notifications"] as const;
export function getNotifications(limit = 30, type?: string): Promise<NotificationList> {
  const q = new URLSearchParams({ limit: String(limit) });
  if (type) q.set("type", type);
  return query(`/v1/notifications?${q.toString()}`, NotificationListSchema);
}
export const mentionsQueryKey = ["notifications", "mention"] as const;
export function getMentions(): Promise<NotificationList> {
  return getNotifications(50, "mention");
}
export function getUnreadCount(): Promise<UnreadCount> {
  return query("/v1/notifications/unread-count", UnreadCountSchema);
}
export function markNotificationRead(id: string): Promise<CommandResult<UnreadCount>> {
  return command(`/v1/notifications/${id}/read`, {}, UnreadCountSchema);
}
export function markAllNotificationsRead(): Promise<CommandResult<UnreadCount>> {
  return command("/v1/notifications/read-all", {}, UnreadCountSchema);
}

// --- Social: mensagens diretas (chat) ---
export const dmThreadsQueryKey = ["dm-threads"] as const;
export const dmMessagesQueryKey = (threadId: string) => ["dm-thread", threadId] as const;
export function getThreads(): Promise<DmThread[]> {
  return query("/v1/dm/threads", DmThreadSchema.array());
}
export function getThreadMessages(threadId: string): Promise<DmMessage[]> {
  return query(`/v1/dm/threads/${threadId}/messages`, DmMessageSchema.array());
}
export function sendDm(
  input: {
    toUserId: string;
    body: string;
  },
  idempotencyKey?: string,
): Promise<CommandResult<DmSendResult>> {
  return command("/v1/dm/messages", input, DmSendResultSchema, idempotencyKey);
}
export function markThreadRead(threadId: string): Promise<CommandResult<UnreadCount>> {
  return command(`/v1/dm/threads/${threadId}/read`, {}, UnreadCountSchema);
}

// --- Vitrine (showcase) ---
export const showcaseQueryKey = ["showcase"] as const;
export function getPublishedShowcase(id: string): Promise<Showcase> {
  return query(`/v1/showcase/by-id/${encodeURIComponent(id)}`, ShowcaseSchema);
}

export function getShowcase(): Promise<Showcase> {
  return query("/v1/showcase", ShowcaseSchema);
}
export function getProgramPreview(programId: string): Promise<ProgramPreview> {
  return query(`/v1/showcase/programs/${programId}/preview`, ProgramPreviewSchema);
}
// Tela do curso do aluno (card da vitrine → /courses/:id/view).
export function getCoursePreview(courseId: string): Promise<CoursePreview> {
  return query(`/v1/showcase/courses/${courseId}/preview`, CoursePreviewSchema);
}
// --- Gamificação ---
export const rankingQueryKey = ["gamification", "ranking"] as const;
export const myGamificationQueryKey = ["gamification", "me"] as const;
export function getRanking(): Promise<RankingEntry[]> {
  return query("/v1/gamification/ranking", RankingEntrySchema.array());
}
export function getMyGamification(): Promise<MyGamification> {
  return query("/v1/gamification/me", MyGamificationSchema);
}
export function getBadges(): Promise<Badge[]> {
  return query("/v1/gamification/badges", BadgeSchema.array());
}
export function createGamificationBadge(
  input: { key: string; name: string; description: string },
  idempotencyKey: string,
): Promise<CommandResult<{ key: string }>> {
  return command("/v1/gamification/badges", input, z.object({ key: z.string() }), idempotencyKey);
}
export function awardGamificationBadge(
  badgeKey: string,
  userId: string,
  idempotencyKey: string,
): Promise<CommandResult<{ awarded: boolean }>> {
  return command(
    `/v1/gamification/badges/${encodeURIComponent(badgeKey)}/award`,
    { userId },
    z.object({ awarded: z.boolean() }),
    idempotencyKey,
  );
}
export function heartbeat(): Promise<CommandResult<StreakResult>> {
  return command("/v1/gamification/heartbeat", {}, StreakResultSchema);
}

// --- Painel admin: dashboard social ---
export const adminDashboardQueryKey = ["admin", "dashboard"] as const;
export function getAdminDashboard(days = 30): Promise<AdminDashboard> {
  return query(`/v1/admin/dashboard?days=${days}`, AdminDashboardSchema);
}
export const adminActivityQueryKey = (from: string, to: string) =>
  ["admin", "activity", from, to] as const;
export function getAdminActivity(from?: string, to?: string): Promise<ActivityPoint[]> {
  const q = new URLSearchParams();
  if (from) q.set("from", from);
  if (to) q.set("to", to);
  const qs = q.toString();
  return query(`/v1/admin/activity${qs ? `?${qs}` : ""}`, ActivityPointSchema.array());
}
// Abas do dashboard.
export const dashboardPostsQueryKey = ["admin", "dashboard", "posts"] as const;
export function getDashboardPosts(): Promise<DashboardPost[]> {
  return query("/v1/admin/dashboard/posts", DashboardPostSchema.array());
}

// --- Admin: Funcionalidades (feature flags) ---
export const featureFlagsQueryKey = ["feature-flags"] as const;
export function getFeatureFlags(): Promise<FeatureFlag[]> {
  return query("/v1/feature-flags", FeatureFlagSchema.array());
}
export function upsertFeatureFlag(
  key: string,
  input: { enabled: boolean; description?: string },
): Promise<CommandResult<FeatureFlag>> {
  return put(`/v1/feature-flags/${key}`, input, FeatureFlagSchema);
}
export function setFeatureFlagOverride(
  key: string,
  input: { subjectType: "user" | "role"; subjectId: string; enabled: boolean },
): Promise<CommandResult<FeatureFlag>> {
  return command(`/v1/feature-flags/${key}/overrides`, input, FeatureFlagSchema);
}
export function removeFeatureFlagOverride(
  key: string,
  input: { subjectType: "user" | "role" | "segment"; subjectId: string },
): Promise<CommandResult<FeatureFlag>> {
  return command(`/v1/feature-flags/${key}/overrides:remove`, input, FeatureFlagSchema);
}

// --- Empresa e marcas autorizadas ---
export const companyQueryKey = ["company"] as const;
export function getCompany(): Promise<CompanyContext> {
  return query("/v1/company", CompanyContextSchema);
}
export function updateCompany(companyId: string, name: string, key: string) {
  return mutate("PUT", "/v1/company", { companyId, name }, CompanySchema, key);
}
export function createCompanyBrand(companyId: string, name: string, key: string) {
  return command("/v1/company/brands", { companyId, name }, PlatformOrganizationSchema, key);
}
export function updateCompanyBrand(
  companyId: string,
  organizationId: string,
  name: string,
  key: string,
) {
  return mutate(
    "PUT",
    `/v1/company/brands/${encodeURIComponent(organizationId)}`,
    { companyId, name },
    PlatformOrganizationSchema,
    key,
  );
}
export function enterCompanyBrand(organizationId: string) {
  return command(
    "/v1/company/session",
    { organizationId },
    z.object({ organizationId: z.string() }),
  );
}
export function grantCompanyAdmin(companyId: string, email: string, key: string) {
  return command(
    "/v1/company/admins/grant",
    { companyId, email },
    z.object({ userId: z.string() }),
    key,
  );
}
export function revokeCompanyAdmin(companyId: string, userId: string, key: string) {
  return command(
    "/v1/company/admins/revoke",
    { companyId, userId },
    z.object({ userId: z.string() }),
    key,
  );
}

export function transferCompanyOwnership(companyId: string, userId: string, key: string) {
  return command(
    "/v1/company/ownership/transfer",
    { companyId, userId },
    z.object({ ownerUserId: z.string() }),
    key,
  );
}

// --- Admin: Personalizar (white-label) ---
export const brandingQueryKey = ["branding"] as const;
export const managementBrandingQueryKey = ["branding-management"] as const;
export function getManagementBranding(): Promise<Branding> {
  return query("/v1/branding/management", BrandingSchema);
}
export function getBranding(): Promise<Branding> {
  const scope = authenticatedBrandScope();
  return query("/v1/branding", BrandingSchema).then((brand) => {
    if (scope && scope === authenticatedBrandScope()) rememberBrand(scope, brand);
    return brand;
  });
}
export function updateBranding(input: {
  colors?: Partial<
    Record<"primary" | "accent" | "pwaBackground" | "loginBackground", string | null>
  >;
  images?: Partial<
    Record<"logo" | "logoLight" | "logoDark" | "favicon" | "loginBanner" | "pwaIcon", string | null>
  >;
  texts?: Partial<
    Record<
      "welcomeTitle" | "loginSubtitle" | "tabTitle" | "pwaName" | "pwaShortName",
      string | null
    >
  >;
  settings?: {
    displayName?: string | null;
    heroBanner?: BrandingSettings["heroBanner"] | null;
    defaultTheme?: BrandingSettings["defaultTheme"];
  };
  logo?: string | null;
}): Promise<CommandResult<Branding>> {
  return patch("/v1/branding", input, BrandingSchema);
}
// Basic info / whitelabel tipado — merge por chave no back (só envia o que mudou).
export function updateBrandingSettings(input: BrandingSettings): Promise<CommandResult<Branding>> {
  return patch("/v1/branding/settings", input, BrandingSchema);
}

// Superfícies anônimas do tenant, resolvidas exclusivamente pelo host.
// Subdomínio da marca (#350): gravar reserva (pending); verificar publica.
export const brandSubdomainQueryKey = ["branding", "subdomain"] as const;
export function getBrandSubdomain(): Promise<BrandSubdomain> {
  return query("/v1/branding/subdomain", BrandSubdomainSchema);
}
export function setBrandSubdomain(subdomain: string): Promise<CommandResult<BrandSubdomain>> {
  return put("/v1/branding/subdomain", { subdomain }, BrandSubdomainSchema);
}
export function verifyBrandSubdomain(): Promise<CommandResult<BrandSubdomain>> {
  return command("/v1/branding/subdomain/verify", {}, BrandSubdomainSchema);
}
export function removeBrandSubdomain(): Promise<void> {
  return remove("/v1/branding/subdomain");
}

export const publicBrandingQueryKey = (host: string) => ["public-branding", host] as const;
export function getPublicBranding(host: string): Promise<PublicBranding> {
  return query(`/v1/public/branding?host=${encodeURIComponent(host)}`, PublicBrandingSchema).then(
    (brand) => {
      rememberBrand(publicBrandScope(host), brand);
      return brand;
    },
  );
}
export const publicPagesQueryKey = (host: string) => ["public-pages", host] as const;
export function getPublicPages(host: string): Promise<PageListItem[]> {
  return query(`/v1/public/pages?host=${encodeURIComponent(host)}`, PageListItemSchema.array());
}
export function getPublicPage(host: string, slug: string): Promise<PageDetail> {
  return query(
    `/v1/public/pages/${encodeURIComponent(slug)}?host=${encodeURIComponent(host)}`,
    PageDetailSchema,
  );
}

// --- Carteira de moedas ---
export const walletMeQueryKey = ["wallet", "me"] as const;
export function getWalletMe(): Promise<WalletMe> {
  return query("/v1/wallet/me", WalletMeSchema);
}
export const adminWalletQueryKey = ["admin", "wallet"] as const;
export function getAdminWallet(): Promise<WalletSummary> {
  return query("/v1/admin/wallet", WalletSummarySchema);
}
export function grantWallet(input: {
  userId: string;
  amount: number;
  reason?: string;
}): Promise<CommandResult<{ userId: string; balance: number }>> {
  return command(
    "/v1/admin/wallet/grant",
    input,
    z.object({ userId: z.string(), balance: z.number() }),
  );
}

// --- Insights: Score dos usuários ---
export interface UserScoreFilters {
  name?: string;
  badge?: string;
  from?: string;
  to?: string;
  scoreMin?: number;
  scoreMax?: number;
  limit?: number;
  offset?: number;
}
export const userScoresQueryKey = ["user-scores"] as const;
export function getUserScores(f: UserScoreFilters = {}): Promise<UserScoreList> {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) if (v !== undefined && v !== "") q.set(k, String(v));
  const qs = q.toString();
  return query(`/v1/admin/user-scores${qs ? `?${qs}` : ""}`, UserScoreListSchema);
}

// --- Comentários em Aulas ---
export const lessonCommentsQueryKey = (lessonId: string) => ["lesson-comments", lessonId] as const;
export function getLessonComments(lessonId: string): Promise<LessonComment[]> {
  return query(`/v1/lessons/${lessonId}/comments`, LessonCommentSchema.array());
}
export function createLessonComment(
  lessonId: string,
  input: { body: string; parentId?: string },
): Promise<CommandResult<LessonComment>> {
  return command(`/v1/lessons/${lessonId}/comments`, input, LessonCommentSchema);
}
export const moderationCommentsQueryKey = ["moderation-comments"] as const;
export function getModerationComments(params: {
  programId?: string;
  lessonId?: string;
  status?: "respondido" | "nao_respondido";
}): Promise<ModerationComment[]> {
  const q = new URLSearchParams();
  if (params.programId) q.set("programId", params.programId);
  if (params.lessonId) q.set("lessonId", params.lessonId);
  if (params.status) q.set("status", params.status);
  const qs = q.toString();
  return query(`/v1/admin/lesson-comments${qs ? `?${qs}` : ""}`, ModerationCommentSchema.array());
}
export function answerLessonComment(
  commentId: string,
  body: string,
): Promise<CommandResult<LessonComment>> {
  return command(`/v1/lesson-comments/${commentId}/answer`, { body }, LessonCommentSchema);
}
export function removeLessonComment(
  commentId: string,
): Promise<CommandResult<{ removed: boolean }>> {
  return command(`/v1/lesson-comments/${commentId}/remove`, {}, z.object({ removed: z.boolean() }));
}
// O próprio autor apaga o comentário (e as respostas a ele).
export function removeOwnLessonComment(
  commentId: string,
): Promise<CommandResult<{ removed: boolean }>> {
  return command(
    `/v1/lesson-comments/${commentId}/remove-own`,
    {},
    z.object({ removed: z.boolean() }),
  );
}
export function markAllCommentsAnswered(): Promise<CommandResult<{ affected: number }>> {
  return command(
    "/v1/admin/lesson-comments/mark-all-answered",
    {},
    z.object({ affected: z.number() }),
  );
}

// --- Tags administráveis ---
export const tagsQueryKey = ["tags"] as const;
export const managementTagsQueryKey = ["tags", "management"] as const;
export function getManagementTags(): Promise<Tag[]> {
  return query("/v1/tags/management", TagSchema.array());
}
export function getTags(): Promise<Tag[]> {
  return query("/v1/tags", TagSchema.array());
}
export function createTag(input: {
  key: string;
  name: string;
  color?: string;
  imageUrl?: string | null;
  requiresPrivilege?: boolean;
  position?: number;
}): Promise<CommandResult<Tag>> {
  return command("/v1/tags", input, TagSchema);
}
export function updateTag(
  id: string,
  input: {
    name?: string;
    color?: string;
    imageUrl?: string | null;
    requiresPrivilege?: boolean;
    archived?: boolean;
    position?: number;
  },
): Promise<CommandResult<Tag>> {
  return patch(`/v1/tags/${id}`, input, TagSchema);
}
export function deleteTag(id: string): Promise<void> {
  return remove(`/v1/tags/${id}`);
}

// --- Admin: Gestão de Usuários ---
export interface AdminUserFilters {
  diagnosisCompleted?: "true" | "false";
  // Busca por parte do nome ou do e-mail (back: UserFilters.q).
  q?: string;
  createdFrom?: string;
  createdTo?: string;
  lastLoginFrom?: string;
  lastLoginTo?: string;
  // Já entrou alguma vez ("true") ou nunca entrou ("false").
  accessed?: "true" | "false";
  // Só quem tem Selo de acesso válido.
  activeAccess?: "true";
  username?: string;
  userId?: string;
  email?: string;
  emailConfirmed?: "true" | "false";
  status?: "active" | "suspended";
  role?: string;
  badge?: string;
  // Quem tem o Selo de acesso (back#174); com o status, só válidos ou vencidos.
  accessBadgeId?: string;
  accessBadgeStatus?: "valid" | "expired";
  limit?: number;
  offset?: number;
}
function usersQuery(f: AdminUserFilters): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) {
    if (v !== undefined && v !== "") p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : "";
}
export function getAdminUsers(f: AdminUserFilters = {}): Promise<AdminUserList> {
  return query(`/v1/admin/users${usersQuery(f)}`, AdminUserListSchema);
}
export interface AssignableUserRole {
  role: "mentorado" | "mentor" | "admin";
  roleId: string | null;
  roleVersion: number;
  name: string;
}
export function getAssignableUserRoles(): Promise<AssignableUserRole[]> {
  return query(
    "/v1/admin/users/grantable-roles",
    z.array(
      z.object({
        role: z.enum(["mentorado", "mentor", "admin"]),
        roleId: z.string().nullable(),
        roleVersion: z.number(),
        name: z.string(),
      }),
    ),
  );
}
export function getAdminUser(userId: string): Promise<AdminUserDetail> {
  return query(`/v1/admin/users/${userId}`, AdminUserDetailSchema);
}

export function getLearnerProgress(userId: string): Promise<LearnerProgress> {
  return query(
    "/v1/admin/users/:userId/learning-progress",
    LearnerProgressSchema,
    { userId },
  );
}

export function getMyJourney(): Promise<LearnerProgress> {
  return query("/v1/me/journey", LearnerProgressSchema);
}
// Exportar: o back devolve o arquivo (CSV, Excel ou PDF — lib/table-export), não
// o envelope {data}. Sem `format`, o back responde CSV.
export type ExportFormat = "csv" | "xlsx" | "pdf";
const EXPORT_ACCEPT: Record<ExportFormat, string> = {
  csv: "text/csv",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  pdf: "application/pdf",
};
async function fetchExport(path: string, format: ExportFormat): Promise<Blob> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    credentials: "include",
    headers: { Accept: EXPORT_ACCEPT[format] },
  });
  reportSessionStatus(res.status);
  if (!res.ok)
    throw new ApiError("EXPORT_FAILED", `export falhou (${res.status})`, res.status, false, null);
  return res.blob();
}
export function exportAdminUsers(
  f: AdminUserFilters = {},
  format: ExportFormat = "csv",
): Promise<Blob> {
  const q = usersQuery(f);
  return fetchExport(`/v1/admin/users/export${q ? `${q}&` : "?"}format=${format}`, format);
}
// Lista de Selos: uma linha por pessoa em cada Selo, com os filtros da tela.
export function exportAccessBadges(
  f: { q?: string; situacao?: "ativos" | "arquivados" | "todos" },
  format: ExportFormat,
): Promise<Blob> {
  const p = new URLSearchParams({ format });
  if (f.q) p.set("q", f.q);
  if (f.situacao) p.set("situacao", f.situacao);
  return fetchExport(`/v1/access-badges/export?${p}`, format);
}
// `invite`: cada conta criada recebe boas-vindas com link de definir senha
// (back#132). Só vai no corpo quando ligado — um back anterior não o conhece.
export function importAdminUsers(
  csv: string,
  options: { invite?: boolean; badgeId?: string } = {},
): Promise<CommandResult<ImportResult>> {
  // badgeId (back#174): as linhas sem a coluna badge recebem este Selo.
  return command(
    "/v1/admin/users/import",
    {
      csv,
      ...(options.invite ? { invite: true } : {}),
      ...(options.badgeId ? { badgeId: options.badgeId } : {}),
    },
    ImportResultSchema,
  );
}
export function updateAdminUser(
  userId: string,
  input: { role?: string; roleId?: string; roleVersion?: number; status?: "active" | "suspended" },
): Promise<CommandResult<AdminUserUpdate>> {
  return patch(`/v1/admin/users/${userId}`, input, AdminUserUpdateSchema);
}
export function removeAdminUser(userId: string): Promise<CommandResult<AdminUserRemoval>> {
  return command(`/v1/admin/users/${userId}/remove`, {}, AdminUserRemovalSchema);
}
// A pessoa entra com a senha temporária e é obrigada a criar uma nova; a
// senha antiga deixa de valer e as sessões dela caem.
export function resetAdminUserPassword(userId: string) {
  return command(`/v1/admin/users/${userId}/temporary-password`, {}, AdminUserTempPasswordSchema);
}

// --- Admin: Publicidades ---
export const adsQueryKey = ["ads"] as const;
export function getServingAds(placement: "feed" | "sidebar" = "feed"): Promise<Ad[]> {
  return query(`/v1/ads/serving?placement=${placement}`, AdSchema.array());
}
export function getLoginAds(): Promise<Ad[]> {
  return query("/v1/ads/login", AdSchema.array());
}
export function getAds(): Promise<Ad[]> {
  return query("/v1/ads", AdSchema.array());
}
export function createAd(input: {
  showOnLogin?: boolean;
  description?: string;
  mediaType?: "image" | "video";
  title: string;
  mediaUrl: string;
  linkUrl: string;
  placement?: "feed" | "sidebar";
}): Promise<CommandResult<Ad>> {
  return command("/v1/ads", input, AdSchema);
}
export function updateAd(
  id: string,
  input: {
    active?: boolean;
    showOnLogin?: boolean;
    title?: string;
    description?: string;
    mediaUrl?: string;
    mediaType?: "image" | "video";
    linkUrl?: string;
    weight?: number;
  },
): Promise<CommandResult<Ad>> {
  return patch(`/v1/ads/${id}`, input, AdSchema);
}

export function removeAd(id: string): Promise<void> {
  return remove(`/v1/ads/${encodeURIComponent(id)}`);
}

// --- Admin: Moderação + Domínios ---
export const moderationActionsQueryKey = ["moderation", "actions"] as const;
export function moderateCommunityContent(
  targetType: "post" | "comment",
  id: string,
  reason: string,
): Promise<CommandResult<{ ok: boolean }>> {
  return command(
    `/v1/moderation/${targetType}s/${id}/remove`,
    { reason },
    z.object({ ok: z.boolean() }),
  );
}
export const domainsQueryKey = ["moderation", "domains"] as const;
export function getModerationActions(): Promise<ModerationAction[]> {
  return query("/v1/moderation/actions", ModerationActionSchema.array());
}
export interface AuditFilters {
  cursor?: string;
  limit?: number;
  actionType?: string;
  actorId?: string;
  resourceType?: string;
  resourceId?: string;
  from?: string;
  to?: string;
  q?: string;
}
function auditQuery(filters: AuditFilters): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== "") params.set(key, String(value));
  }
  const queryString = params.toString();
  return queryString ? `?${queryString}` : "";
}
export function getAuditEvents(filters: AuditFilters = {}): Promise<AuditPage> {
  return query(`/v1/audit/events${auditQuery(filters)}`, AuditPageSchema);
}
export function getAuditEvent(id: string): Promise<AuditEvent> {
  return query(`/v1/audit/events/${id}`, AuditEventSchema);
}
export function reviewAuditEvent(
  id: string,
  input: { decision: "approved" | "rejected"; reason: string },
): Promise<CommandResult<AuditReview>> {
  return command(`/v1/audit/events/${id}/review`, input, AuditReviewSchema);
}
export function suspendUser(
  userId: string,
  reason?: string,
): Promise<CommandResult<{ suspended: boolean }>> {
  return command(
    `/v1/moderation/users/${userId}/suspend`,
    { reason },
    z.object({ suspended: z.boolean() }),
  );
}
export function restoreUser(userId: string): Promise<CommandResult<{ suspended: boolean }>> {
  return command(
    `/v1/moderation/users/${userId}/restore`,
    {},
    z.object({ suspended: z.boolean() }),
  );
}
export function getDomains(): Promise<string[]> {
  return query("/v1/moderation/domains", z.string().array());
}
export function addDomain(domain: string): Promise<CommandResult<{ domain: string }>> {
  return command("/v1/moderation/domains", { domain }, z.object({ domain: z.string() }));
}
export function removeDomain(domain: string): Promise<CommandResult<{ removed: boolean }>> {
  return command("/v1/moderation/domains:remove", { domain }, z.object({ removed: z.boolean() }));
}
export function checkDomain(email: string): Promise<DomainCheck> {
  return query(
    `/v1/moderation/domains/check?email=${encodeURIComponent(email)}`,
    DomainCheckSchema,
  );
}

// --- Admin: Insights ---
export const insightsQueryKey = (kind: string) => ["insights", kind] as const;
export function getInsights(kind: string): Promise<InsightDefinition[]> {
  return query(`/v1/insights?kind=${kind}`, InsightDefinitionSchema.array());
}
export function upsertInsight(input: {
  kind: string;
  key: string;
  name: string;
  definition: Record<string, unknown>;
}): Promise<CommandResult<InsightDefinition>> {
  return command("/v1/insights", input, InsightDefinitionSchema);
}
// Resultado do "Avaliar" (back insights/controller.ts#evaluate), um formato por
// tipo. Fora do contrato vira erro, não JSON cru na tela (front#662).
const count = z.number().int().nonnegative();
export const InsightEvaluationSchema = z.union([
  z.object({ key: z.string(), value: count }),
  z.object({ key: z.string(), size: count }),
  z.object({ key: z.string(), steps: z.array(z.object({ step: z.string(), count })) }),
]);
export type InsightEvaluation = z.infer<typeof InsightEvaluationSchema>;
export function evaluateInsight(kind: string, key: string): Promise<InsightEvaluation> {
  return command(`/v1/insights/${kind}/${key}/evaluate`, {}, InsightEvaluationSchema).then(
    (r) => r.data,
  );
}

// --- Admin: Integrações ---
export const apiKeysQueryKey = ["integrations", "api-keys"] as const;
export const webhooksQueryKey = ["integrations", "webhooks"] as const;
export const automationsQueryKey = ["integrations", "automations"] as const;
export const apiScopesQueryKey = ["integrations", "scopes"] as const;
export function getApiScopes(): Promise<ApiScope[]> {
  return query("/v1/integrations/scopes", ApiScopeSchema.array());
}
export function getApiKeys(): Promise<ApiKey[]> {
  return query("/v1/integrations/api-keys", ApiKeySchema.array());
}
export function createApiKey(
  label: string,
  scopes: string[],
): Promise<CommandResult<ApiKeyCreated>> {
  return command("/v1/integrations/api-keys", { label, scopes }, ApiKeyCreatedSchema);
}
export function revokeApiKey(id: string): Promise<CommandResult<{ revoked: boolean }>> {
  return command(`/v1/integrations/api-keys/${id}/revoke`, {}, z.object({ revoked: z.boolean() }));
}
export function getWebhooks(): Promise<OutboundWebhook[]> {
  return query("/v1/integrations/webhooks", OutboundWebhookSchema.array());
}
export function createWebhook(input: {
  url: string;
  events: string[];
  secret: string;
  filters?: { accessBadgeId?: string };
}): Promise<CommandResult<{ id: string }>> {
  return command("/v1/integrations/webhooks", input, z.object({ id: z.string() }));
}
// Entregas de um webhook (back#187): ver, reenviar e enviar teste.
export const webhookDeliveriesQueryKey = (webhookId: string, status?: WebhookDelivery["status"]) =>
  ["integrations", "webhooks", webhookId, "deliveries", status ?? "all"] as const;
export function getWebhookDeliveries(
  webhookId: string,
  status?: WebhookDelivery["status"],
): Promise<WebhookDelivery[]> {
  const qs = status ? `?status=${status}` : "";
  return query(
    `/v1/integrations/webhooks/${webhookId}/deliveries${qs}`,
    WebhookDeliverySchema.array(),
  );
}
export function retryWebhookDelivery(
  webhookId: string,
  deliveryId: string,
): Promise<CommandResult<WebhookDelivery>> {
  return command(
    `/v1/integrations/webhooks/${webhookId}/deliveries/${deliveryId}/retry`,
    {},
    WebhookDeliverySchema,
  );
}
export function sendWebhookTest(webhookId: string): Promise<CommandResult<WebhookDelivery>> {
  return command(`/v1/integrations/webhooks/${webhookId}/test`, {}, WebhookDeliverySchema);
}
export function getAutomations(): Promise<Automation[]> {
  return query("/v1/integrations/automations", AutomationSchema.array());
}
export function createAutomation(input: {
  name: string;
  trigger: string;
  action: Record<string, unknown>;
}): Promise<CommandResult<{ id: string }>> {
  return command("/v1/integrations/automations", input, z.object({ id: z.string() }));
}
export function getZoomStatus(): Promise<ZoomStatus> {
  return query("/v1/integrations/zoom/status", ZoomStatusSchema);
}

// --- MCP: chaves do servidor MCP ---
export const mcpKeysQueryKey = ["mcp", "keys"] as const;
export function getMcpKeys(): Promise<McpKey[]> {
  return query("/v1/mcp/keys", McpKeySchema.array());
}
export function getMcpTargets() {
  return query(
    "/v1/mcp/keys/targets",
    z.array(z.object({ userId: z.string(), name: z.string(), email: z.string() })),
  );
}
export function getMcpCatalog() {
  return query(
    "/v1/mcp/keys/catalog",
    z.array(
      z.object({
        name: z.string(),
        group: z.string(),
        kind: z.enum(["read", "write"]),
        description: z.string(),
        confirmation: z.boolean(),
        feature: z.string().optional(),
        actions: z.array(z.string()),
      }),
    ),
  );
}
export function createMcpKey(
  input: {
    name: string;
    userId?: string;
    readOnly: boolean;
    expiresAt: string;
    organizationId: string;
  },
  idempotencyKey: string,
): Promise<CommandResult<McpKeyCreated>> {
  return command("/v1/mcp/keys", input, McpKeyCreatedSchema, idempotencyKey);
}
export function revokeMcpKey(
  id: string,
  idempotencyKey?: string,
): Promise<CommandResult<{ revoked: boolean }>> {
  return command(
    `/v1/mcp/keys/${id}/revoke`,
    {},
    z.object({ revoked: z.boolean() }),
    idempotencyKey,
  );
}

// --- Admin: Institucional ---
export const pagesQueryKey = ["pages"] as const;
export const emailTemplatesQueryKey = ["email-templates"] as const;
export const mobileMenuQueryKey = ["mobile-menu"] as const;
export function getPages(): Promise<PageListItem[]> {
  return query("/v1/pages", PageListItemSchema.array());
}
export function getPage(slug: string): Promise<PageDetail> {
  return query(`/v1/pages/${slug}`, PageDetailSchema);
}
export function upsertPage(input: {
  slug: string;
  title: string;
  body?: string;
  published?: boolean;
  nav?: PageNav;
}): Promise<CommandResult<{ slug: string }>> {
  return command("/v1/pages", input, z.object({ slug: z.string() }));
}
export function createPage(input: { slug: string; title: string; body?: string; nav?: PageNav }) {
  return command("/v1/pages/create", input, z.object({ slug: z.string() }));
}
export function updatePage(slug: string, input: { title: string; body?: string; nav?: PageNav }) {
  return patch(`/v1/pages/${slug}`, input, z.object({ slug: z.string() }));
}
export function setPageStatus(slug: string, published: boolean) {
  return command(`/v1/pages/${slug}/status`, { published }, z.object({ slug: z.string() }));
}
export function getEmailTemplates(): Promise<EmailTemplateListItem[]> {
  return query("/v1/email-templates", EmailTemplateListItemSchema.array());
}
export function upsertEmailTemplate(input: {
  key: string;
  subject: string;
  body?: string;
}): Promise<CommandResult<{ key: string }>> {
  return command("/v1/email-templates", input, z.object({ key: z.string() }));
}
export function createEmailTemplate(input: { key: string; subject: string; body?: string }) {
  return command("/v1/email-templates/create", input, z.object({ key: z.string() }));
}
export function updateEmailTemplate(key: string, input: { subject: string; body?: string }) {
  return patch(`/v1/email-templates/${key}`, input, z.object({ key: z.string() }));
}
export function renderEmailTemplate(
  key: string,
  vars: Record<string, string>,
): Promise<RenderedTemplate> {
  return command(`/v1/email-templates/${key}/render`, { vars }, RenderedTemplateSchema).then(
    (r) => r.data,
  );
}
export function getMobileMenu(): Promise<MobileMenu> {
  return query("/v1/mobile-menu", MobileMenuSchema);
}
export function setMobileMenu(items: MobileMenuItem[]): Promise<CommandResult<MobileMenu>> {
  return put("/v1/mobile-menu", { items }, MobileMenuSchema);
}

// --- Gestão ---
export const programsQueryKey = ["programs"] as const;
export function getPrograms(): Promise<Program[]> {
  return query("/v1/programs", ProgramSchema.array());
}
export function createProgram(input: {
  name: string;
  description?: string;
  spaceId?: string | null;
  coverUrl?: string | null;
}): Promise<CommandResult<Program>> {
  return command("/v1/programs", input, ProgramSchema);
}
export function getProgram(id: string): Promise<Program> {
  return query(`/v1/programs/${encodeURIComponent(id)}`, ProgramSchema);
}
export function updateProgram(
  id: string,
  input: {
    name?: string;
    description?: string;
    spaceId?: string | null;
    hero?: ProgramHero;
    coverUrl?: string | null;
  },
): Promise<CommandResult<Program>> {
  return patch(`/v1/programs/${id}`, input, ProgramSchema);
}
// Vitrine do programa: hero + cursos como cards.
export function getProgramVitrine(id: string): Promise<ProgramVitrine> {
  return query(`/v1/programs/${id}/vitrine`, ProgramVitrineSchema);
}
export function activateProgram(id: string): Promise<CommandResult<Program>> {
  return command(`/v1/programs/${id}/activate`, {}, ProgramSchema);
}
export function archiveProgram(id: string): Promise<CommandResult<Program>> {
  return command(`/v1/programs/${id}/archive`, {}, ProgramSchema);
}
// Só trilha arquivada e sem curso ativo nem turma com histórico (o back recusa com 409).
export function deleteProgram(id: string): Promise<void> {
  return remove(`/v1/programs/${id}`);
}

export function getCurricula(programId: string): Promise<CurriculumVersion[]> {
  return query(`/v1/programs/${programId}/curricula`, CurriculumVersionSchema.array());
}
export function getCurriculumVersion(
  programId: string,
  versionId: string,
): Promise<CurriculumVersion> {
  return query(`/v1/programs/${programId}/curricula/${versionId}`, CurriculumVersionSchema);
}
export function publishCurriculum(
  programId: string,
  content: CurriculumContent,
): Promise<CommandResult<CurriculumVersion>> {
  return command(
    `/v1/programs/${programId}/curricula:publish`,
    { content },
    CurriculumVersionSchema,
  );
}

// --- Cursos (a unidade de currículo; um programa opcional os agrupa) ---
export const coursesQueryKey = ["courses"] as const;
export function getCourses(): Promise<Course[]> {
  return query("/v1/courses", CourseSchema.array());
}
// Cursos de uma trilha (programa).
export function getProgramCourses(programId: string): Promise<Course[]> {
  return query(`/v1/programs/${programId}/courses`, CourseSchema.array());
}
export function createCourse(input: {
  name: string;
  description?: string;
  programId?: string | null;
  spaceId?: string | null;
  order?: number;
  coverUrl?: string | null;
  presentation?: CoursePresentation;
  settings?: CourseSettings;
}, idempotencyKey?: string): Promise<CommandResult<Course>> {
  return command("/v1/courses", input, CourseSchema, idempotencyKey);
}
export function getCourse(id: string): Promise<Course> {
  return query(`/v1/courses/${id}`, CourseSchema);
}
export function updateCourse(
  id: string,
  input: {
    name?: string;
    description?: string;
    programId?: string | null;
    spaceId?: string | null;
    order?: number;
    coverUrl?: string | null;
    presentation?: CoursePresentation;
    settings?: CourseSettings;
  },
): Promise<CommandResult<Course>> {
  return patch(`/v1/courses/${id}`, input, CourseSchema);
}
export function activateCourse(
  id: string,
  idempotencyKey?: string,
): Promise<CommandResult<Course>> {
  return command(`/v1/courses/${id}/activate`, {}, CourseSchema, idempotencyKey);
}
export function archiveCourse(id: string): Promise<CommandResult<Course>> {
  return command(`/v1/courses/${id}/archive`, {}, CourseSchema);
}
export function getCourseCurricula(courseId: string): Promise<CurriculumVersion[]> {
  return query(`/v1/courses/${courseId}/curricula`, CurriculumVersionSchema.array());
}
export function getCourseCurriculumVersion(
  courseId: string,
  versionId: string,
): Promise<CurriculumVersion> {
  return query(`/v1/courses/${courseId}/curricula/${versionId}`, CurriculumVersionSchema);
}
// Uma cópia por ocorrência, na ordem enviada; aulas e materiais independentes.
export function cloneCourseLessons(courseId: string, lessonIds: string[], idempotencyKey: string) {
  return command(
    `/v1/courses/${courseId}/lessons:clone`,
    { lessonIds },
    z.array(z.object({ sourceLessonId: z.string(), lesson: LessonSchema })),
    idempotencyKey,
  );
}

export function publishCourseCurriculum(
  courseId: string,
  content: CurriculumContent,
  idempotencyKey?: string,
): Promise<CommandResult<CurriculumVersion>> {
  return command(
    `/v1/courses/${courseId}/curricula:publish`,
    { content },
    CurriculumVersionSchema,
    idempotencyKey,
  );
}

export const cohortsQueryKey = ["cohorts"] as const;
export function getCohorts(): Promise<Cohort[]> {
  return query("/v1/cohorts", CohortSchema.array());
}
export function createCohort(input: {
  programId: string;
  curriculumVersionId: string;
  name: string;
  startsAt: string;
  endsAt: string;
}): Promise<CommandResult<Cohort>> {
  return command("/v1/cohorts", input, CohortSchema);
}
export function getCohort(id: string): Promise<Cohort> {
  return query(`/v1/cohorts/${id}`, CohortSchema);
}
export function updateCohort(
  id: string,
  input: { name?: string; startsAt?: string; endsAt?: string },
): Promise<CommandResult<Cohort>> {
  return patch(`/v1/cohorts/${id}`, input, CohortSchema);
}
export function cohortTransition(
  id: string,
  action: "open" | "complete" | "cancel",
): Promise<CommandResult<Cohort>> {
  return command(`/v1/cohorts/${id}/${action}`, {}, CohortSchema);
}

export const enrollmentsQueryKey = ["enrollments"] as const;
export function getEnrollments(): Promise<Enrollment[]> {
  return query("/v1/enrollments", EnrollmentSchema.array());
}
export function enroll(input: {
  cohortId: string;
  userId: string;
  source: "manual" | "webinar" | "crm" | "import" | "api";
}): Promise<CommandResult<Enrollment>> {
  return command("/v1/enrollments", input, EnrollmentSchema);
}
export function getEnrollment(id: string): Promise<Enrollment> {
  return query(`/v1/enrollments/${id}`, EnrollmentSchema);
}
export function enrollmentTransition(
  id: string,
  action: "pause" | "resume" | "complete" | "cancel",
): Promise<CommandResult<Enrollment>> {
  return command(`/v1/enrollments/${id}/${action}`, {}, EnrollmentSchema);
}
// Roster de uma turma (matrículas + vínculo de acesso).
export function getRoster(cohortId: string): Promise<Enrollment[]> {
  return query(`/v1/enrollments?cohortId=${cohortId}`, EnrollmentSchema.array());
}

// --- Acesso (convites / provisionamento) ---
export const invitesQueryKey = ["access-invites"] as const;
export function getInvites(): Promise<Invite[]> {
  return query("/v1/access/invites", InviteSchema.array());
}
export function getGrantableRoles() {
  return query(
    "/v1/access/roles",
    z.array(
      z.object({
        id: z.string(),
        name: z.string(),
        role: z.enum(["mentorado", "mentor", "admin"]),
        version: z.number(),
      }),
    ),
  );
}
export function createInvite(input: {
  role?: string;
  cohortId?: string;
  email?: string;
  expiresInDays?: number;
  roleId?: string;
  roleVersion?: number;
  organizationId?: string;
}, idempotencyKey?: string) {
  return command("/v1/access/invites", input, CreateInviteResultSchema, idempotencyKey);
}
export function revokeInvite(id: string): Promise<CommandResult<Invite>> {
  return command(`/v1/access/invites/${id}/revoke`, {}, InviteSchema);
}
// Público: dados do convite para a página /join (sem sessão).
export function getInviteByToken(token: string): Promise<InvitePublic> {
  return query(`/v1/access/invites/token/${token}`, InvitePublicSchema);
}
// Cadastro pelo convite (back#191): cria a conta e aceita numa transação. O
// cadastro público do Better Auth está fechado. E-mail que já tem conta → erro
// ACCOUNT_EXISTS: a tela leva a entrar e aceitar (acceptInvite).
export function registerWithInvite(
  token: string,
  input: { name: string; password: string; email?: string; emailConfirmation?: string },
  idempotencyKey?: string,
): Promise<CommandResult<{ organizationId: string; email: string }>> {
  return command(
    `/v1/access/invites/token/${token}/register`,
    input,
    z.object({ organizationId: z.string(), email: z.string() }),
    idempotencyKey,
  );
}
export function acceptInvite(
  token: string,
  idempotencyKey?: string,
): Promise<CommandResult<{ organizationId: string }>> {
  return command(
    `/v1/access/invites/token/${token}/accept`,
    {},
    z.object({ organizationId: z.string() }),
    idempotencyKey,
  );
}
export function provisionMentee(input: {
  email: string;
  name: string;
  role?: string;
  cohortId?: string;
  roleId?: string;
  roleVersion?: number;
  organizationId?: string;
}, idempotencyKey?: string): Promise<CommandResult<ProvisionResult>> {
  return command("/v1/access/provision", input, ProvisionResultSchema, idempotencyKey);
}

// --- Conteúdo ---
export const lessonsQueryKey = ["lessons"] as const;
export function getLessons(): Promise<Lesson[]> {
  return query("/v1/lessons", LessonSchema.array());
}
export function getLesson(id: string): Promise<Lesson> {
  return query(`/v1/lessons/${id}`, LessonSchema);
}
// mediaKind: video | audio | article | pdf. Vídeos: upload | vimeo | youtube; artigo: none.
export interface LessonInput {
  categoryIds?: string[];
  chapterDraftId?: string;
  chapterReview?: import("@/lesson-chapters-contract").ChapterReview;
  title: string;
  description?: string;
  mediaKind: string;
  mediaProvider: string;
  mediaUrl: string;
  body?: string;
  durationSeconds: number;
  order: number;
  freeClass: boolean;
  allowComments: boolean;
  thumbnailUrl?: string;
}
export function createLesson(input: LessonInput): Promise<CommandResult<Lesson>> {
  return command("/v1/lessons", input, LessonSchema);
}
export function updateLesson(
  id: string,
  input: Partial<LessonInput>,
): Promise<CommandResult<Lesson>> {
  return patch(`/v1/lessons/${id}`, input, LessonSchema);
}
export function publishLesson(id: string, idempotencyKey?: string): Promise<CommandResult<Lesson>> {
  return command(`/v1/lessons/${id}/publish`, {}, LessonSchema, idempotencyKey);
}
// Despublica (published → draft): tira do ar e reabre a aula para edição.
export function unpublishLesson(id: string): Promise<CommandResult<Lesson>> {
  return command(`/v1/lessons/${id}/unpublish`, {}, LessonSchema);
}
export function recordWatch(
  lessonId: string,
  input: { watchedSeconds: number; lastPositionSeconds: number },
): Promise<CommandResult<Watch>> {
  return command(`/v1/lessons/${lessonId}/watch`, input, WatchSchema);
}
export function getWatch(lessonId: string): Promise<Watch | null> {
  return query(`/v1/me/lessons/${lessonId}/watch`, WatchSchema.nullable());
}

// Materiais de apoio (anexos baixáveis) de uma aula.
export function getMaterials(lessonId: string): Promise<LessonMaterial[]> {
  return query(`/v1/lessons/${lessonId}/materials`, LessonMaterialSchema.array());
}
export function addMaterial(
  lessonId: string,
  input: { role?: string; kind: string; title: string; url: string; sizeBytes?: number },
): Promise<CommandResult<LessonMaterial>> {
  return command(`/v1/lessons/${lessonId}/materials`, input, LessonMaterialSchema);
}
export function deleteMaterial(lessonId: string, materialId: string): Promise<void> {
  return remove(`/v1/lessons/${lessonId}/materials/${materialId}`);
}
// Registra que o aluno baixou o material (métrica). Disparado no clique.
export function recordMaterialDownload(
  lessonId: string,
  materialId: string,
): Promise<CommandResult<{ ok: boolean }>> {
  return command(
    `/v1/lessons/${lessonId}/materials/${materialId}/download`,
    {},
    z.object({ ok: z.boolean() }),
  );
}
// Métrica (mentor): quem baixou o material.
export function getMaterialDownloads(
  lessonId: string,
  materialId: string,
): Promise<MaterialDownloadStat[]> {
  return query(
    `/v1/lessons/${lessonId}/materials/${materialId}/downloads`,
    MaterialDownloadStatSchema.array(),
  );
}

// Presign de upload (o back gera a URL PUT; o front sobe direto no storage).
// `purpose` deriva a pasta/validação do tipo (video|audio|thumbnail|material).
// Recebe o arquivo: checa a política (tamanho/tipo, com a mensagem para o admin)
// antes de pedir a URL e manda o tamanho, que o back assina junto com o tipo —
// por isso o PUT (uploadFile) tem de usar o mesmo file.type.
export type { UploadPurpose };
const UploadTargetSchema = z.object({
  uploadUrl: z.string(),
  objectUrl: z.string(),
  key: z.string(),
});
export async function requestUpload(
  file: File,
  purpose: UploadPurpose = "video",
  ownership: "personal" | "organization" = "organization",
  adId?: string,
): Promise<{ uploadUrl: string; objectUrl: string; key: string }> {
  checkUpload(file, purpose);
  const r = await command(
    "/v1/lessons/uploads",
    {
      filename: file.name,
      contentType: file.type,
      sizeBytes: file.size,
      purpose,
      ownership,
      ...(adId ? { adId } : {}),
    },
    UploadTargetSchema,
  );
  return r.data;
}
// Sobe o arquivo direto no storage (PUT na URL pré-assinada), com progresso.
export function uploadFile(
  uploadUrl: string,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrl);
    // O mesmo tipo pedido em requestUpload: ele está assinado na URL.
    xhr.setRequestHeader("content-type", file.type);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`upload falhou (HTTP ${xhr.status})`));
    xhr.onerror = () => reject(new Error("upload falhou (rede)"));
    xhr.send(file);
  });
}

// Imagem pública (logo, banner, imagem do Selo): pede a URL pré-assinada com o
// propósito "thumbnail" (PNG/JPEG/WebP até 5 MB), sobe e devolve a URL final.
export async function uploadImage(file: File): Promise<string> {
  const ticket = await requestUpload(file, "thumbnail");
  await uploadFile(ticket.uploadUrl, file);
  return ticket.objectUrl;
}

// --- Outcome Engine ---
export const outcomesQueryKey = ["outcomes"] as const;
export function getOutcomes(): Promise<OutcomeContract[]> {
  return query("/v1/outcome-contracts", OutcomeContractSchema.array());
}
export function acceptOutcome(id: string): Promise<CommandResult<OutcomeContract>> {
  return command(`/v1/outcome-contracts/${id}/accept`, {}, OutcomeContractSchema);
}
// Unidades padronizadas (back#704, U1): o back recusa outra ao propor.
export const OUTCOME_UNITS = ["R$/mês", "clientes/mês", "vendas/mês", "%"] as const;
export type OutcomeUnit = (typeof OUTCOME_UNITS)[number];
// Propor contrato de resultado (back outcomes_propose): nasce "proposto".
export function proposeOutcome(
  input: {
    subjectId: string;
    problem: string;
    baseline: number;
    target: number;
    unit: OutcomeUnit;
    why: string;
    where: string;
    who: string;
    how: string;
    description: string;
    pillar: "planning" | "sales" | "marketing" | "leadership" | "management";
    metricSource: string;
    acceptedEvidence: string;
    deadline: string;
    reviewCadence: "weekly" | "biweekly" | "monthly";
    // Formulário preenchido pelo rascunho da IA (27/10): a origem vai junto.
    origin?: "ia";
  },
  idempotencyKey: string,
): Promise<CommandResult<OutcomeContract>> {
  return command("/v1/outcome-contracts", input, OutcomeContractSchema, idempotencyKey);
}
// 5W2H (back#656), a descrição (back#714) e o pilar (back#703): um item por
// vez, com antes e depois na Auditoria.
export const PLAN_FIELDS = [
  "description",
  "pillar",
  "problem",
  "why",
  "where",
  "who",
  "how",
  "deadline",
  "baseline",
  "target",
  "unit",
  "metricSource",
  "acceptedEvidence",
  "reviewCadence",
] as const;
export type PlanField = (typeof PLAN_FIELDS)[number];
export function editOutcomePlan(
  id: string,
  // origin "ia": texto aplicado da atualização pela IA (back#715), na Auditoria.
  input: { field: PlanField; value: string | number | null; origin?: "ia" } | {changes: {field:PlanField;value:string|number|null}[]},
  idempotencyKey: string,
): Promise<CommandResult<OutcomeContract>> {
  return patch(
    `/v1/outcome-contracts/${encodeURIComponent(id)}/plan`,
    input,
    OutcomeContractSchema,
    idempotencyKey,
  );
}
export const PlanHistorySchema = z.object({
  action: z.string(),
  at: z.string(),
  by: z.object({ id: z.string(), name: z.string().nullable() }),
  detail: z.string(),
});
export type PlanHistoryEntry = z.infer<typeof PlanHistorySchema>;
export const outcomeHistoryQueryKey = (id: string) => ["outcomes", id, "history"] as const;
// Encerrar o planejamento aberto (back#717): mentor e admin, com o motivo.
export function closeOutcome(
  id: string,
  reason: string,
  idempotencyKey: string,
): Promise<CommandResult<OutcomeContract>> {
  return command(
    `/v1/outcome-contracts/${encodeURIComponent(id)}/close`,
    { reason },
    OutcomeContractSchema,
    idempotencyKey,
  );
}
export function getOutcomeHistory(id: string): Promise<PlanHistoryEntry[]> {
  return query(
    `/v1/outcome-contracts/${encodeURIComponent(id)}/history`,
    PlanHistorySchema.array(),
  );
}
export const outcomeObservationsQueryKey = (id: string) => ["outcomes", id, "observations"] as const;
export function getOutcomeObservations(id: string): Promise<MetricObservation[]> {
  return query(`/v1/outcome-contracts/${id}/observations`, MetricObservationSchema.array());
}
export function recordOutcomeObservation(
  id: string,
  input: { value: number; observedAt: string; source: string; note?: string },
  idempotencyKey: string,
): Promise<CommandResult<{ observation: MetricObservation; contract: OutcomeContract }>> {
  // O back recalcula o estado do contrato na mesma escrita e devolve os dois.
  return command(
    `/v1/outcome-contracts/${id}/observations`,
    input,
    z.object({ observation: MetricObservationSchema, contract: OutcomeContractSchema }),
    idempotencyKey,
  );
}

export const commitmentsQueryKey = ["commitments"] as const;
export function getCommitments(): Promise<Commitment[]> {
  return query("/v1/commitments", CommitmentSchema.array());
}
export function acceptCommitment(id: string): Promise<CommandResult<Commitment>> {
  return command(`/v1/commitments/${id}/accept`, {}, CommitmentSchema);
}
// Marcar como feita (back#663, D1): a evidência só é obrigatória quando a meta
// a exige; sem ela, vai sem corpo de evidência.
export function submitCommitmentEvidence(
  id: string,
  evidence: string,
  idempotencyKey: string,
): Promise<CommandResult<Commitment>> {
  return command(
    `/v1/commitments/${id}/evidence`,
    evidence ? { evidence } : {},
    CommitmentSchema,
    idempotencyKey,
  );
}

// Desmarcar (D1): só a própria pessoa; a meta volta a aceita.
export function reopenCommitment(
  id: string,
  idempotencyKey: string,
): Promise<CommandResult<Commitment>> {
  return command(`/v1/commitments/${id}/reopen`, {}, CommitmentSchema, idempotencyKey);
}

// Criar, editar e cancelar (D3, D4). Na edição, só os campos enviados mudam;
// null limpa os opcionais. Cancelar tira a meta da lista e guarda o histórico.
export interface CommitmentFields {
  title: string;
  description: string | null;
  evidenceRequired: string | null;
  deadline: string | null;
  outcomeContractId: string;
}
export function proposeCommitment(
  input: { subjectId: string } & CommitmentFields,
  idempotencyKey: string,
): Promise<CommandResult<Commitment>> {
  // A criação não aceita null: o campo vazio fica de fora.
  const body = Object.fromEntries(Object.entries(input).filter(([, v]) => v !== null));
  return command("/v1/commitments", body, CommitmentSchema, idempotencyKey);
}
export function editCommitment(
  id: string,
  changes: Partial<CommitmentFields>,
  idempotencyKey: string,
): Promise<CommandResult<Commitment>> {
  return patch(`/v1/commitments/${id}`, changes, CommitmentSchema, idempotencyKey);
}
export function cancelCommitment(
  id: string,
  reason: string,
  idempotencyKey: string,
): Promise<CommandResult<Commitment>> {
  return command(
    `/v1/commitments/${id}/cancel`,
    reason ? { reason } : {},
    CommitmentSchema,
    idempotencyKey,
  );
}

// Revisar a evidência de uma meta enviada (back#669): aprovar, rejeitar ou
// pedir correção, com nota. Só com learning.commitments.review.
export type CommitmentDecision = "approve" | "reject" | "request_correction";
export function reviewCommitment(
  id: string,
  decision: CommitmentDecision,
  note: string,
  idempotencyKey: string,
): Promise<CommandResult<Commitment>> {
  return command(
    `/v1/commitments/${id}/review`,
    { decision, ...(note ? { note } : {}) },
    CommitmentSchema,
    idempotencyKey,
  );
}

export const applicablesQueryKey = ["applicables"] as const;
export function getApplicables(): Promise<Applicable[]> {
  return query("/v1/applicables", ApplicableSchema.array());
}
export function submitApplicable(id: string): Promise<CommandResult<Applicable>> {
  return command(`/v1/applicables/${id}/submit`, {}, ApplicableSchema);
}
export function reviewApplicable(
  id: string,
  decision: "approve" | "reject",
): Promise<CommandResult<Applicable>> {
  return command(`/v1/applicables/${id}/review`, { decision }, ApplicableSchema);
}

export const interventionsQueryKey = ["interventions"] as const;
export function getInterventions(): Promise<InterventionCase[]> {
  return query("/v1/interventions", InterventionCaseSchema.array());
}
export function approveIntervention(id: string): Promise<CommandResult<InterventionCase>> {
  return command(`/v1/interventions/${id}/approve`, {}, InterventionCaseSchema);
}
export function executeIntervention(id: string): Promise<CommandResult<InterventionCase>> {
  return command(`/v1/interventions/${id}/execute`, {}, InterventionCaseSchema);
}

// --- Lead Twin ---
export const twinsQueryKey = ["twins"] as const;
export function getTwins(): Promise<Twin[]> {
  return query("/v1/twins", TwinSchema.array());
}

// --- Helena ---
export const proposalsQueryKey = ["helena", "proposals"] as const;
export function getProposals(): Promise<Proposal[]> {
  return query("/v1/advisory/proposals", ProposalSchema.array());
}
export function decideProposal(
  id: string,
  decision: "approve" | "reject",
): Promise<CommandResult<Proposal>> {
  return command(`/v1/advisory/proposals/${id}/decision`, { decision }, ProposalSchema);
}

// --- Negócio ---

export const analyticsQueryKey = ["analytics", "overview"] as const;
export function getAnalyticsOverview(): Promise<AnalyticsOverview> {
  return query("/v1/analytics/overview", AnalyticsOverviewSchema);
}
// Trilhas ativas e aulas publicadas/em rascunho para a Visão geral de
// Indicadores (os números que estavam na antiga aba Cursos do Painel, #662).
export const analyticsCoursesQueryKey = ["analytics", "courses"] as const;
export function getAnalyticsCourses(): Promise<AnalyticsCourses> {
  return query("/v1/admin/dashboard/courses", AnalyticsCoursesSchema);
}

// --- Vitrines múltiplas (admin) · #105 fase 1 ---
//
// Superfície do CRUD em /v1/showcases (PLURAL). O /v1/showcase singular acima é
// a leitura pública e continua existindo — os dois convivem.
export const showcasesQueryKey = ["showcases"] as const;
export const showcaseRecordQueryKey = (id: string) => ["showcases", id] as const;
export const showcasePreviewQueryKey = (id: string) =>
  [...showcaseQueryKey, "preview", id] as const;

export function getResolvedShowcase(id: string): Promise<Showcase> {
  return query(`/v1/showcases/${encodeURIComponent(id)}/resolved`, ShowcaseSchema);
}

export function getShowcases(): Promise<ShowcaseRecord[]> {
  return query("/v1/showcases", ShowcaseRecordSchema.array());
}

export function getShowcaseRecord(id: string): Promise<ShowcaseRecord> {
  return query(`/v1/showcases/${id}`, ShowcaseRecordSchema);
}

export function createShowcase(input: {
  name: string;
  slug: string;
  status?: ShowcaseStatus;
}): Promise<CommandResult<ShowcaseRecord>> {
  return command("/v1/showcases", input, ShowcaseRecordSchema);
}

// PUT substitui blocos e aparência por inteiro (molde do PUT da config antiga).
// Os metadados são opcionais para o construtor salvar só o canvas.
export function updateShowcaseRecord(
  id: string,
  input: {
    name?: string;
    slug?: string;
    status?: ShowcaseStatus;
    sections?: Block[];
    appearance?: ShowcaseAppearance;
  },
): Promise<CommandResult<ShowcaseRecord>> {
  return put(`/v1/showcases/${id}`, input, ShowcaseRecordSchema);
}

export function setShowcaseStatus(
  id: string,
  status: ShowcaseStatus,
): Promise<CommandResult<ShowcaseRecord>> {
  return command(`/v1/showcases/${id}/status`, { status }, ShowcaseRecordSchema);
}

export function duplicateShowcase(
  id: string,
  input: { name?: string; slug?: string } = {},
): Promise<CommandResult<ShowcaseRecord>> {
  return command(`/v1/showcases/${id}/duplicate`, input, ShowcaseRecordSchema);
}

// Rota própria, e não um campo do PUT: trocar o que o público vê é decisão de
// publicação, não efeito colateral de salvar o canvas.
export function makeShowcaseDefault(id: string): Promise<CommandResult<ShowcaseRecord>> {
  return command(`/v1/showcases/${id}/default`, {}, ShowcaseRecordSchema);
}

export function deleteShowcase(id: string): Promise<void> {
  return remove(`/v1/showcases/${id}`);
}

// ── Selos de acesso (back#151/#157) ─────────────────────────────────────────
// Só admin (capability education.access-badges.manage). Toda mutação passa por
// command/patch/put/remove (Idempotency-Key + traceparent).
export type { AccessBadge, AccessBadgeDetail, AccessBadgeGrant };
export const myHomeBadgesQueryKey = ["my-home-badges"] as const;
export function getMyHomeBadges() {
  return query("/v1/me/access-badges", MyHomeBadgeSchema.array());
}

export const accessBadgesQueryKey = ["access-badges"] as const;
export const accessBadgeQueryKey = (id: string) => ["access-badges", id] as const;
export const accessBadgeGrantsQueryKey = (id: string) => ["access-badges", id, "grants"] as const;

export function getAccessBadges(): Promise<AccessBadge[]> {
  return query("/v1/access-badges", AccessBadgeSchema.array());
}
export function getAccessBadge(id: string): Promise<AccessBadgeDetail> {
  return query(`/v1/access-badges/${id}`, AccessBadgeDetailSchema);
}
export function createAccessBadge(input: {
  name: string;
  description?: string;
  imageUrl?: string | null;
  relevance: number;
  alwaysVisible?: boolean;
  homeLink?: HomeLink | null;
  terms?: FavoriteTerm[];
}): Promise<CommandResult<AccessBadge>> {
  return command("/v1/access-badges", input, AccessBadgeSchema);
}
export function updateAccessBadge(
  id: string,
  input: {
    name?: string;
    description?: string;
    imageUrl?: string | null;
    relevance?: number;
    alwaysVisible?: boolean;
    homeLink?: HomeLink | null;
    terms?: FavoriteTerm[];
    accessTerm?: { months: number } | { days: number } | null;
  },
): Promise<CommandResult<AccessBadge>> {
  return patch(`/v1/access-badges/${id}`, input, AccessBadgeSchema);
}
export function deleteAccessBadge(id: string): Promise<void> {
  return remove(`/v1/access-badges/${id}`);
}
// Arquivar / desarquivar (back#235, F4): encerra o Selo sem destruir os direitos.
export function archiveAccessBadge(id: string): Promise<CommandResult<AccessBadge>> {
  return command(`/v1/access-badges/${id}/archive`, {}, AccessBadgeSchema);
}
export function unarchiveAccessBadge(id: string): Promise<CommandResult<AccessBadge>> {
  return command(`/v1/access-badges/${id}/unarchive`, {}, AccessBadgeSchema);
}
// Substitui o conjunto inteiro de cursos e programas que o Selo libera.
export function setAccessBadgeContents(
  id: string,
  input: { courseIds: string[]; programIds: string[] },
): Promise<CommandResult<{ courseIds: string[]; programIds: string[] }>> {
  return put(`/v1/access-badges/${id}/contents`, input, AccessBadgeContentsSchema);
}
export type AccessBadgeHistoryFilters = {
  limit?: number;
  action?: "granted" | "renewed" | "revoked" | "expired";
  source?: string;
  from?: string;
  to?: string;
};
export const accessBadgeHistoryQueryKey = (id: string, f: AccessBadgeHistoryFilters) =>
  ["access-badges", id, "history", f] as const;
// Histórico das concessões (back#172), mais recente primeiro, por cursor.
export function getAccessBadgeHistory(
  id: string,
  f: AccessBadgeHistoryFilters & { cursor?: string },
): Promise<z.infer<typeof AccessBadgeHistoryPageSchema>> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) if (v) qs.set(k, String(v));
  const q = qs.toString();
  return query(`/v1/access-badges/${id}/history${q ? `?${q}` : ""}`, AccessBadgeHistoryPageSchema);
}
export const myAccessBadgesQueryKey = ["me", "access-badges"] as const;
// Os Selos do próprio aluno, com a validade (back#151; tela na fatia 7b, #152).
export function getMyAccessBadges(): Promise<z.infer<typeof MyAccessBadgeSchema>[]> {
  return query("/v1/me/access-badges", z.array(MyAccessBadgeSchema));
}
export const accessBadgeOriginsQueryKey = (id: string) => ["access-badges", id, "origins"] as const;
// De onde vieram as concessões (back#173): ações em massa e importações recentes.
export function getAccessBadgeOrigins(
  id: string,
): Promise<z.infer<typeof AccessBadgeOriginsSchema>> {
  return query(`/v1/access-badges/${id}/origins`, AccessBadgeOriginsSchema);
}
export type AccessBadgeGrantFilters = {
  q?: string;
  source?: string;
  status?: "valid" | "expired";
};
// Quem tem o Selo (back#173): busca, origem e validade no servidor, por cursor.
// A chave base (accessBadgeGrantsQueryKey) continua o prefixo de invalidação.
export function getAccessBadgeGrants(
  id: string,
  f: AccessBadgeGrantFilters & { cursor?: string },
): Promise<z.infer<typeof AccessBadgeGrantPageSchema>> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) if (v) qs.set(k, v);
  const q = qs.toString();
  return query(`/v1/access-badges/${id}/grants${q ? `?${q}` : ""}`, AccessBadgeGrantPageSchema);
}
// Dar ou renovar: prazo em meses (o servidor calcula, fiel ao dia do cadastro),
// data (fim do dia em Brasília, ISO) ou null (sem validade). No passado → 422.
export function grantAccessBadge(
  id: string,
  input: { userId: string; expiresAt?: string | null; term?: Term },
): Promise<CommandResult<{ userId: string; expiresAt: string | null; renewed: boolean }>> {
  return command(`/v1/access-badges/${id}/grants`, input, AccessBadgeGrantResultSchema);
}
// Atribuir a vários (back#174): a mesma concessão, numa transação — ou todos
// recebem, ou nenhum (422 dizendo quantos não são da marca).
export function grantAccessBadges(
  id: string,
  input: { userIds: string[]; expiresAt?: string | null; term?: Term },
): Promise<
  CommandResult<{ items: Array<{ userId: string; expiresAt: string | null; renewed: boolean }> }>
> {
  return command(`/v1/access-badges/${id}/grants`, input, AccessBadgeGrantManyResultSchema);
}
// Ação em massa sobre quem tem o Selo VÁLIDO (back#192). A ação escolhe o
// formato dos parâmetros; o registro em access-badges/bulk-actions.ts monta.
export type BulkActionInput =
  | { action: "space.add" | "space.remove"; params: { spaceId: string } }
  | { action: "badge.add"; params: { badgeId: string; term?: Term; expiresAt?: string | null } }
  | { action: "badge.remove"; params: { badgeId: string } }
  | { action: "coins.add" | "coins.remove"; params: { amount: number; reason?: string } };
export function runAccessBadgeBulkAction(
  id: string,
  input: BulkActionInput,
): Promise<CommandResult<BulkActionResult>> {
  return command(`/v1/access-badges/${id}/bulk-actions`, input, BulkActionResultSchema);
}
export function revokeAccessBadgeGrant(id: string, userId: string): Promise<void> {
  return remove(`/v1/access-badges/${id}/grants/${userId}`);
}

export function sendInviteSignupProof(token: string, email: string) {
  return command(
    `/v1/access/invites/token/${token}/registration-proof`,
    { email },
    z.object({ sent: z.boolean() }),
  );
}
